import { useEffect, useRef, useState } from 'react';
import {
  InputGroup,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import { Competition } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { queryClient } from '@/services/api/queryClient';
import { useDeleteCompetitionMutation } from '@/services/queries/competition';
import { useTranslation } from 'react-i18next';

export interface CompetitionEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  competitions: Competition[] | undefined;
  onConfirm: (data: ModalFormData) => void;
  onClose: () => void;
}

export const CompetitionEditModal = ({ isOpen, competitions, onConfirm, onClose }: CompetitionEditModalProps) => {
  const { t } = useTranslation();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Competition | undefined>(undefined);

  const { mutate: deleteMutation, isLoading: isLoadingDeletePosition } = useDeleteCompetitionMutation();
  const toast = useToast();

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      names: [],
    },
    resolver: zodResolver(modalFormSchema),
    criteriaMode: 'all',
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'names',
  });
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = (data: ModalFormData) => {
    onConfirm?.(data);
    reset();
  };

  const handleConfirm = () => {
    formRef?.current?.requestSubmit();
  };

  const handleClose = () => {
    clearErrors();
    reset();
    onClose?.();
  };

  const handleDeleteItem = () => {
    if (!selectedItem) return;

    deleteMutation(selectedItem?.uuid, {
      onSuccess: async () => {
        toast({
          title: t('pages.application_won_title.components.competition_edit_modal.competition_edit_delete_success'),
          status: 'success',
        });

        setIsConfirmModalOpen(false);
        setSelectedItem(undefined);
        await queryClient.invalidateQueries(['GET_COMPETITION']);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title:
            (resp && resp.message) || t('pages.application_won_title.components.competition_edit_modal.competition_edit_delete_error'),
        });
      },
    });
  };

  useEffect(() => {
    reset();
  }, [reset]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
              <Text fontSize='medium' fontWeight='bold'>
                {t('pages.application_won_title.components.competition_edit_modal.competition_edit_title')}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalBody>
            <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
              {competitions?.length ? (
                <Flex direction='column' gap={6}>
                  {competitions?.map(element => (
                    <Flex direction='column' gap={2} key={element?.uuid}>
                      <Text>{t('pages.application_won_title.components.competition_edit_modal.competition_edit_name_label')}</Text>
                      <Flex gap={1}>
                        <Text w={{ base: '100%', md: '160px' }}>{element?.name}</Text>

                        <ChakraFeatherIcon
                          name='trash-2'
                          color='red.500'
                          cursor='pointer'
                          onClick={() => {
                            setSelectedItem(element);
                            setIsConfirmModalOpen(true);
                            onClose?.();
                          }}
                        />
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ) : null}

              {fields?.length ? (
                <Flex direction='column' gap={6}>
                  {fields?.map((field, index) => (
                    <InputGroup key={field.id} display='flex' flexDirection='column'>
                      <Text>{t('pages.application_won_title.components.competition_edit_modal.competition_edit_name_label')}</Text>

                      <Flex alignItems='center' gap={2}>
                        <InputField
                          withoutFloating
                          rootProps={{
                            isInvalid: !!errors?.names?.[index]?.name,
                            errorMessage: errors?.names?.[index]?.name?.message,
                            w: { base: '100%', md: '260px' },
                          }}
                          {...register(`names.${index}.name`)}
                        />

                        <ChakraFeatherIcon name='trash-2' color='red.500' cursor='pointer' onClick={() => remove(index)} />
                      </Flex>
                    </InputGroup>
                  ))}
                </Flex>
              ) : null}

              <Button
                alignSelf='flex-start'
                variant='link'
                color='brand.500'
                textDecor='underline'
                display='flex'
                alignItems='center'
                gap={2}
                onClick={() => append({ name: '' })}
              >
                <ChakraFeatherIcon name='plus' />
                {t('pages.application_won_title.components.competition_edit_modal.competition_edit_add_button')}
              </Button>
            </Flex>
          </ModalBody>

          <ModalFooter justifyContent='flex-end' gap='kilo'>
            <Flex gap={2}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_won_title.components.competition_edit_modal.competition_edit_cancel_button')}
              </Button>
              <Button onClick={handleConfirm}>
                {t('pages.application_won_title.components.competition_edit_modal.competition_edit_submit_button')}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={handleDeleteItem}
        isLoading={isLoadingDeletePosition}
        title={t('pages.application_won_title.components.competition_edit_modal.competition_edit_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_won_title.components.competition_edit_modal.competition_edit_delete_confirm')}{' '}
            <Text as='span' fontWeight='bold'>
              {selectedItem?.name}
            </Text>
            ?
          </Text>
        }
      />
    </>
  );
};
